import { createElement, useLayoutEffect, useRef } from 'react';
import { SearchResponseDeviceType } from '@sitecore-search/data';
import useContentBlock from './hooks/useContentBlock.js';
/**
 * Adds a style elemt to a specific node. If the style already exists, it will be updated.
 * We're working with the DOM, so we need to be careful with the side effects.
 * @param {string | undefined} rfkId - The id used to identify the widget.
 * @param {HTMLElement} node - The node where the style will be added.
 * @param {string} css - The CSS rules to be added.
 */
function addStyle(rfkId, node, css) {
    let styleNode = document.querySelector(`[data-rfk-id-style=${rfkId}]`);
    if (!styleNode) {
        styleNode = document.createElement('style');
        styleNode.setAttribute('type', 'text/css');
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        styleNode.dataset.rfkIdStyle = rfkId;
        node.parentNode?.appendChild(styleNode);
    }
    styleNode.innerHTML = css;
}
/**
 * Removes a style element from the DOM.
 * @see addStyle
 */
function removeStyle(rfkId) {
    const styleNode = document.querySelector(`[data-rfk-id-style=${rfkId}]`);
    if (styleNode) {
        styleNode.remove();
    }
}
/**
 * A React component that renders a content block widget, including the HTML and CSS content to the page.
 * When the component is unmounted, the CSS styles are removed from the page.
 */
const ContentBlock = ({ initializer }) => {
    const { rfkId, widgetRef, queryResult: { data: { appearance: { css_names: cssNames = [], templates: { html: { devices: { [SearchResponseDeviceType.DESKTOP]: { content: htmlContent = null } = {} } = {} } = {}, css: { devices: { [SearchResponseDeviceType.DESKTOP]: { content: cssContent = null } = {} } = {} } = {}, } = {}, } = {}, } = {}, }, } = useContentBlock({
        query: initializer || (() => { }),
    });
    const contentRef = useRef(null);
    // sets widget styles
    useLayoutEffect(() => {
        if (contentRef.current && cssContent) {
            addStyle(rfkId, contentRef.current, cssContent);
        }
        return () => {
            removeStyle(rfkId);
        };
    }, [rfkId, cssContent]);
    // sets widget content
    useLayoutEffect(() => {
        if (contentRef.current && htmlContent) {
            contentRef.current.innerHTML = htmlContent;
        }
    }, [htmlContent]);
    return createElement('div', { 'ref': widgetRef, 'data-rfkid': rfkId, 'className': cssNames.join(' ') }, createElement('div', { ref: contentRef }));
};
export default ContentBlock;
